import { Container, Graphics, Text, TextStyle } from "pixi.js";
import { PianoLayout, KeyLayout } from "@/lib/piano/piano-layout";
import { PianoComponent } from "@/lib/studio/view/components/piano-component";
import { MusicalTimeline } from "@/lib/music/musical-timeline";
import { Note } from "@/lib/music/note";
import { NoteEntry } from "./notes";

export type NoteHintsProps = {
  entries: NoteEntry[];
  currentTick: number;
  playheadY: number;
  fromTick: number;
  toTick: number;
  width: number;
  height: number;
  /** Per-laneIdx fill/stroke colors (resolved upstream from LaneView). */
  laneColors: readonly { fill: number; stroke: number }[];
};

const HINT_RADIUS = 5;
const HINT_Y_OFFSET = 5;
const HINT_BLACK_KEY_EXTRA_Y = 18;
const HINT_PADDING_X = 9;
const HINT_PADDING_Y = 5;

const LABEL_STYLE_BASE = {
  fontSize: 14,
  fontWeight: "600" as const,
};

class NoteHint {
  container: Container;
  private background: Graphics;
  private label: Text;

  constructor(parent: Container) {
    this.container = new Container();
    this.background = new Graphics();
    this.label = new Text({ text: "", style: new TextStyle(LABEL_STYLE_BASE) });
    this.container.addChild(this.background);
    this.container.addChild(this.label);
    parent.addChild(this.container);
  }

  draw(cx: number, y: number, noteName: string, laneColor: number, alpha: number): void {
    this.label.text = noteName;
    this.label.style.fill = laneColor;

    const labelW = this.label.width;
    const labelH = this.label.height;
    const boxWidth = labelW + HINT_PADDING_X * 2;
    const boxHeight = labelH + HINT_PADDING_Y * 2;

    this.background
      .clear()
      .roundRect(0, 0, boxWidth, boxHeight, HINT_RADIUS)
      .fill({ color: laneColor, alpha: 0.15 })
      .stroke({ color: laneColor, alpha: 0.20, width: 1 });

    this.label.x = HINT_PADDING_X;
    this.label.y = HINT_PADDING_Y;

    this.container.x = Math.round(cx - boxWidth / 2);
    this.container.y = y;
    this.container.alpha = alpha;
    this.container.visible = true;
  }

  hide(): void {
    this.container.visible = false;
  }

  destroy(): void {
    this.label.destroy();
    this.background.destroy();
    this.container.destroy();
  }
}

export class NoteHints {
  private container: Container;
  private pool: NoteHint[] = [];
  private poolIndex = 0;

  constructor(
    stage: Container,
    private layout: PianoLayout,
    private pianoComponent: PianoComponent,
    private musicalTimeline: MusicalTimeline,
  ) {
    this.container = new Container();
    stage.addChild(this.container);
  }

  setMusicalTimeline(musicalTimeline: MusicalTimeline): void {
    this.musicalTimeline = musicalTimeline;
  }

  draw(props: NoteHintsProps): void {
    const { entries, currentTick, playheadY, laneColors } = props;

    this.poolIndex = 0;

    // Find the nearest upcoming tick per lane
    const nearestTickPerLane = new Map<number, number>();
    for (const entry of entries) {
      const distance = entry.tick - currentTick;
      if (distance <= 0) continue;
      const current = nearestTickPerLane.get(entry.laneIdx);
      if (current === undefined || entry.tick < current) {
        nearestTickPerLane.set(entry.laneIdx, entry.tick);
      }
    }

    for (const entry of entries) {
      if (entry.tick !== nearestTickPerLane.get(entry.laneIdx)) {
        continue;
      }

      const { playedNote, laneIdx } = entry;
      const midi = playedNote.note.toMidi();
      const keyLayout = this.layout.getKeyByMidi(midi);
      if (!keyLayout) continue;

      const cx = this.getNoteCenter(keyLayout);
      const isBlackKey = keyLayout.color === "black";
      const y = playheadY + HINT_Y_OFFSET + (isBlackKey ? HINT_BLACK_KEY_EXTRA_Y : 0);
      const noteName = Note.fromMidi(midi).toName();
      const fallbackColor = laneColors[laneIdx % Math.max(1, laneColors.length)];
      const laneColor = (laneColors[laneIdx] ?? fallbackColor).fill;

      const hint = this.acquire();
      hint.draw(cx, y, noteName, laneColor, 1);
    }

    for (let i = this.poolIndex; i < this.pool.length; i++) {
      this.pool[i].hide();
    }
  }

  destroy(): void {
    for (const hint of this.pool) {
      hint.destroy();
    }
    this.pool.length = 0;
    this.poolIndex = 0;
    this.container.destroy({ children: true });
  }

  private getNoteCenter(keyLayout: KeyLayout): number {
    return this.pianoComponent.getProjection().keyCenter(keyLayout);
  }

  private acquire(): NoteHint {
    if (this.poolIndex < this.pool.length) {
      return this.pool[this.poolIndex++];
    }

    const hint = new NoteHint(this.container);
    this.pool.push(hint);
    this.poolIndex++;
    return hint;
  }
}
